import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Clock, CheckCircle2, XCircle, Ban, Hourglass, Square } from "lucide-react";
import { ACTION_LABELS } from "@/components/pages/workflows/WorkflowConstants";
import type { WorkflowExecution, WorkflowExecutionStepStatus } from "@/model/interface/Workflow";
import type PagedResponse from "@/model/interface/PagedResponse";

const STEP_STATUS_ICONS: Record<WorkflowExecutionStepStatus, React.ReactNode> = {
  PENDING: <Clock className="h-3.5 w-3.5 text-muted-foreground" />,
  RUNNING: <Play className="h-3.5 w-3.5 text-blue-500" />,
  WAITING: <Hourglass className="h-3.5 w-3.5 text-amber-500" />,
  COMPLETED: <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />,
  FAILED: <XCircle className="h-3.5 w-3.5 text-red-500" />,
  SKIPPED: <Ban className="h-3.5 w-3.5 text-muted-foreground" />,
};

const EXECUTION_STATUS_COLORS: Record<string, string> = {
  RUNNING: "bg-blue-500",
  COMPLETED: "bg-green-500",
  FAILED: "bg-red-500",
  CANCELLED: "bg-gray-500",
};

interface ExecutionListProps {
  data?: PagedResponse<WorkflowExecution>;
  page: number;
  onPageChange: (page: number) => void;
  onCancel: (executionId: string) => void;
}

const ExecutionList: React.FC<ExecutionListProps> = ({ data, page, onPageChange, onCancel }) => {
  const executions = data?.items ?? [];

  if (executions.length === 0) {
    return <p className="text-sm text-muted-foreground">No executions yet</p>;
  }

  return (
    <div className="space-y-3">
      {executions.map((execution) => (
        <div key={execution.id} className="border rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge className={`text-xs text-white ${EXECUTION_STATUS_COLORS[execution.status] || "bg-gray-500"}`}>
                {execution.status}
              </Badge>
              {execution.contact && (
                <span className="text-sm">
                  {[execution.contact.firstName, execution.contact.lastName].filter(Boolean).join(" ")}
                </span>
              )}
              {execution.deal && (
                <span className="text-xs text-muted-foreground">
                  {execution.deal.propertyAddress}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {execution.triggeredAt && (
                <span className="text-xs text-muted-foreground">
                  {new Date(execution.triggeredAt).toLocaleString()}
                </span>
              )}
              {execution.status === "RUNNING" && (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onCancel(execution.id)}>
                  <Square className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
              )}
            </div>
          </div>
          {execution.error && (
            <p className="text-xs text-red-500 mb-2">{execution.error}</p>
          )}
          <div className="flex items-center gap-3">
            {execution.steps.map((step) => (
              <div
                key={step.id}
                className="flex items-center gap-1"
                title={`${ACTION_LABELS[step.actionType]}: ${step.status}${step.error ? ` - ${step.error}` : ""}`}
              >
                {STEP_STATUS_ICONS[step.status]}
                <span className="text-xs text-muted-foreground">{ACTION_LABELS[step.actionType]}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {data && data.totalPages > 1 && (
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => onPageChange(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={data.last} onClick={() => onPageChange(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
};

export default ExecutionList;
